import { ContactBlacklistedProspect } from "../../Contacts/schema/contacts";

type BlacklistOrigin = NonNullable<ContactBlacklistedProspect["blacklistOrigin"]>;
type BlacklistDetectedBy = NonNullable<ContactBlacklistedProspect["blacklistDetectedBy"]>;

/**
 * The blacklist type's label and pill colour, keyed by origin. Extracted from the column renderer
 * so the reasoning hover can head itself with the same wording the pill shows (PREC-3594).
 */
const ORIGIN_LABEL_KEYS: Record<BlacklistOrigin, string> = {
	UNSUBSCRIBED_BY_LEAD: "blacklist.originUnsubscribed",
	BLACKLISTED_BY_USER: "blacklist.originBlacklistedByUser",
	IMPORTED_BY_USER: "blacklist.originImportedByUser",
	COMPETITOR: "blacklist.originCompetitor",
};

const ORIGIN_PILL_CLASSES: Record<BlacklistOrigin, string> = {
	UNSUBSCRIBED_BY_LEAD: "bg-orange-50 text-orange-700 border-orange-200",
	BLACKLISTED_BY_USER: "bg-red-50 text-red-700 border-red-200",
	IMPORTED_BY_USER: "bg-brand-white text-text border-border-light",
	COMPETITOR: "bg-purple-50 text-purple-700 border-purple-200",
};

const DETECTED_BY_LABEL_KEYS: Record<BlacklistDetectedBy, string> = {
	AI: "blacklist.detectedByAi",
	MANUAL: "blacklist.detectedByManual",
	LEAD: "blacklist.detectedByLead",
};

export const blacklistOriginLabelKey = (origin?: string | null): string =>
	ORIGIN_LABEL_KEYS[origin as BlacklistOrigin] ?? "blacklist.originUnknown";

export const blacklistOriginPillClass = (origin?: string | null): string =>
	ORIGIN_PILL_CLASSES[origin as BlacklistOrigin] ?? "bg-gray-50 text-gray-700 border-gray-200";

export const blacklistDetectedByLabelKey = (detectedBy?: string | null): string | null =>
	DETECTED_BY_LABEL_KEYS[detectedBy as BlacklistDetectedBy] ?? null;

/**
 * The one-line attribution shown above the reasoning: who decided, and — when a person did — who
 * that was. Returns null when the row carries no attribution at all (a list-only or snoozed row).
 */
export const blacklistAttributionLine = (
	ts: (key: string) => string,
	row: Pick<ContactBlacklistedProspect, "blacklistDetectedBy" | "blacklistAddedBy">
): string | null => {
	const detectedByKey = blacklistDetectedByLabelKey(row.blacklistDetectedBy);

	if (!detectedByKey) {
		return null;
	}

	const detectedBy = ts(detectedByKey);

	return row.blacklistAddedBy ? `${detectedBy} · ${row.blacklistAddedBy}` : detectedBy;
};
